/*
 * Size- and stock-aware product cards for the collection grid.
 *
 * A collection card normally shows the product's default colour. Once a size filter is
 * active that is wrong: the card can show a colour that does not come in the shopper's size.
 * Each card carries a compact variant map (built in snippets/mstore-product-card.liquid) with
 * the size values once, per colour its image plus a "size:variantId" map, and - because
 * Shopify's own Availability facet calls a sell-on-with-no-stock variant "available" - the
 * sizes that are genuinely in stock ("ready") and the sizes that can only be pre-ordered
 * ("preorder"). Given the active filters this script:
 *
 *   - hides products that have no variant in that size
 *   - switches the card image to a colour that actually has the size
 *   - replaces the swatch row with only the colours that have the size, each linking to the
 *     PDP with that colour's variant for the selected size
 *   - applies the shop's own availability meaning: "In stock" keeps only products that are
 *     ready to ship, "Out of stock" keeps only the pre-orderable ones. Both are decided in the
 *     browser, so the requests below deliberately leave availability out and the whole
 *     catalogue is classified here instead of by Shopify's facet.
 *
 * It also closes the gap that exists until the Search & Discovery option facet is switched on:
 * without a facet the server cannot filter by size, so a page holds only 24 of the catalogue's
 * products and the rest of the matches are invisible. When a size is active this script asks
 * Shopify for the remaining collection pages (Section Rendering API, so only the section comes
 * back), rebuilds the grid in collection order and keeps every matching product. The result is
 * cached per tab, so changing size or stock does not refetch.
 *
 * Once the facet exists the section reports data-ms-server-filtered="true" and this script
 * skips the loading entirely - the server has already returned exactly the matching products.
 *
 * The active size is read from the native facet parameter (filter.v.option.<option name>) or
 * from a plain ?size= parameter, so the behaviour works before the facet is switched on as well
 * as after it.
 */
(() => {
  const CARD = '.ms-card[data-card-variants]';
  const GRID = '[data-ms-grid]';
  const GRID_CLASS = 'ms-product-grid';
  const PILL = '.ms-count-pill';
  const MAX_PAGES = 12; // safety cap: beyond this the grid would be unusable anyway
  // v3: v2 entries were kept for the life of the tab with no timestamp, so a browser that had
  // loaded the collection once kept filtering stale card maps after the catalogue changed.
  const CACHE_PREFIX = 'msSizeCards:v3:';
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const AVAILABILITY = 'filter.v.availability';

  const compactSize = (value) => String(value || '').replace(/\s*\([^)]*\)\s*$/, '').trim();

  // Grid links carry Shopify's collection tracking parameters (_pos, _fid, _ss), so a card's
  // href is not a clean handle - building "/products/<href>?variant=" out of it produces a
  // broken URL with the tracking query and a doubled ?variant=.
  const handleFrom = (href) =>
    String(href || '').split('?')[0].split('#')[0].split('/products/')[1] || '';

  const isSizeParam = (key) => key === 'size' || key.indexOf('filter.v.option.') === 0;

  const readActiveSize = () => {
    const params = new URLSearchParams(window.location.search);
    const wanted = [];
    for (const [key, value] of params.entries()) {
      if (isSizeParam(key)) wanted.push(value);
    }
    return wanted.map((value) => String(value).trim().toLowerCase()).filter(Boolean);
  };

  // the same value, kept in the case the shopper chose, for display
  const readActiveSizeLabel = () => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params.entries()) {
      if (isSizeParam(key)) return compactSize(value);
    }
    return '';
  };

  // "1" is the facet's In stock value, "0" its Out of stock value
  const readActiveAvailability = () =>
    new URLSearchParams(window.location.search).getAll(AVAILABILITY).filter(Boolean);

  // every other filter the shopper has applied (price, ...), for the cache key. Availability is
  // not in here: the pages we fetch and cache are availability-free by design, so one cached set
  // serves every stock choice.
  const readFilterKey = () =>
    Array.from(new URLSearchParams(window.location.search).entries())
      .filter(([key]) => key.indexOf('filter.') === 0 && !isSizeParam(key) && key !== AVAILABILITY)
      .sort()
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

  // Ask the CDN for a square, centre-cropped thumbnail. Appending a second `width` to a URL that
  // already carries one is ignored, so the swatch used to download the full-size scene photo and
  // shrink it - which is why a 26px circle showed a whole room instead of a product. Replace the
  // params rather than appending, and request the crop the circle needs.
  const imageUrl = (url, width, height) => {
    const [base, query] = String(url).split('?');
    const params = new URLSearchParams(query || '');
    params.set('width', String(width));
    if (height) {
      params.set('height', String(height));
      params.set('crop', 'center');
    }
    return `${base}?${params.toString()}`;
  };

  const swatchHtml = (handle, color, variantId, active) => {
    const image = color.image
      ? `<img src="${imageUrl(color.image, 120)}" alt="" loading="lazy" width="34" height="34">`
      : '';
    const label = color.name || 'Colour';
    return `<a class="ms-card__swatch${active ? ' is-active' : ''}" href="/products/${handle}?variant=${variantId}" title="${label}" aria-label="${label}">${image}</a>`;
  };

  // the card is a div around its link (the quick-add button sits beside the link, not inside it)
  const cardLink = (card) => card.querySelector('.ms-card__link') || card;
  const cardKey = (card) => (cardLink(card).getAttribute('href') || '').split('?')[0];

  const cardData = (card) => {
    try {
      return JSON.parse(card.getAttribute('data-card-variants') || '');
    } catch (error) {
      return null;
    }
  };

  // --- availability, in the shop's own terms -------------------------------------------------
  // A product with no variant map (no size option) cannot be judged from the map, so fall back to
  // the badges the card already renders: pre-order and sold-out are both decided server-side.
  const badgeAvailability = (card) => ({
    ready: !card.querySelector('.ms-card__badge--preorder') && !card.querySelector('.ms-card__badge--muted'),
    preorder: Boolean(card.querySelector('.ms-card__badge--preorder')),
  });

  // Pure: does this product satisfy the chosen availability, in the chosen size?
  //   "In stock"     -> a variant with real stock (or unmanaged inventory)
  //   "Out of stock" -> a variant that sells on with no stock, i.e. pre-orderable
  // The card map carries arrays of sizes; the badge fallback below carries booleans, so both
  // shapes are accepted here.
  const availabilityMatch = (buckets, sizeValue, availability) => {
    if (!availability || !availability.length) return true;
    const wantReady = availability.indexOf('1') > -1;
    const wantPreorder = availability.indexOf('0') > -1;

    const readyList = Array.isArray(buckets && buckets.ready) ? buckets.ready : [];
    const preorderList = Array.isArray(buckets && buckets.preorder) ? buckets.preorder : [];
    const readyAny = (buckets && buckets.ready === true) || readyList.length > 0;
    const preorderAny = (buckets && buckets.preorder === true) || preorderList.length > 0;

    const sizeReady = sizeValue ? readyList.indexOf(sizeValue) > -1 : readyAny;
    const sizePreorder = sizeValue ? preorderList.indexOf(sizeValue) > -1 : preorderAny;
    return (wantReady && sizeReady) || (wantPreorder && sizePreorder);
  };

  // --- size matching: returns null when the product has no variant in the size -----------
  const sizeMatch = (card, sizes) => {
    const data = cardData(card);
    if (!data || !Array.isArray(data.sizes) || !Array.isArray(data.colors)) return null;

    // match the requested size against this product's own size values ("M8/W10" also matches
    // "M8/W10 (41-42)")
    let index = -1;
    for (const wanted of sizes) {
      index = data.sizes.findIndex((value) => String(value).trim().toLowerCase() === wanted);
      if (index === -1) {
        index = data.sizes.findIndex((value) => {
          const short = compactSize(value).toLowerCase();
          return short === wanted || short === compactSize(wanted).toLowerCase();
        });
      }
      if (index > -1) break;
    }
    if (index === -1) return null;

    const sizeValue = data.sizes[index];
    const entries = data.colors
      .map((color) => {
        const parts = String(color.map || '').split('|');
        const hit = parts.find((part) => part.slice(0, part.lastIndexOf(':')) === sizeValue);
        return hit ? { color, variantId: hit.slice(hit.lastIndexOf(':') + 1) } : null;
      })
      .filter(Boolean);

    // the size matched, which is what decides membership: a product with no colour option has no
    // colour entries to show, but it still belongs in the grid. Entries decide presentation only.
    return { sizeValue, entries, buckets: data };
  };

  // One source of truth for "what does this card's stock look like": its own size-aware map when it
  // has one, otherwise the rendered badges. Filtering and counting both go through it.
  const stockFor = (card) => {
    const map = cardData(card);
    const hasBuckets = Boolean(map && (Array.isArray(map.ready) || Array.isArray(map.preorder)));
    return hasBuckets ? map : badgeAvailability(card);
  };

  // --- contextual facet counts ---------------------------------------------------------------
  // The server counts each availability option over the whole collection, so with a size picked its
  // numbers describe a different result set than the grid. Recompute both options - and each size -
  // from the loaded cards using the same tests the grid uses, so a count and the grid it promises
  // can never disagree. Each option is counted as if it were the choice, so the shopper can see
  // what the other one would give before clicking it.
  const countMatches = (cards, sizes, availability, pick) => {
    let total = 0;
    cards.forEach((card) => {
      const match = sizes.length ? sizeMatch(card, sizes) : null;
      if (sizes.length && !match) return;
      const sizeValue = match ? match.sizeValue : null;
      const stock = stockFor(card);
      const choice = pick(sizeValue);
      if (availabilityMatch(stock, sizeValue, choice)) total += 1;
    });
    return total;
  };

  const renderFacetCounts = (cards, sizes, availability) => {
    if (!cards || !cards.length) return;
    const grid = document.querySelector(GRID);
    // with a native size facet the server did the counting itself and its numbers already include it
    if (grid && grid.dataset.msServerFiltered === 'true') return;

    document.querySelectorAll(`input[name="${AVAILABILITY}"]`).forEach((input) => {
      const value = String(input.value);
      if (value !== '1' && value !== '0') return;
      const row = input.closest('label') || input.parentElement;
      const node = row ? row.querySelector(`[data-ms-facet-count="${value}"]`) : null;
      if (!node) return;
      const count = countMatches(cards, sizes, availability, () => [value]);
      node.textContent = `${node.getAttribute('data-ms-label') || ''} (${count})`;
      node.classList.toggle('ms-facet-count--empty', count === 0);
      // the server disables an option it counted as empty; a zero from data it could not see must
      // not lock the shopper out of a choice that is really there
      if (count > 0 && input.disabled) input.disabled = false;
    });

    const sizeControl = document.querySelector('[data-ms-size-filter]');
    const sizeParam = sizeControl ? sizeControl.getAttribute('data-ms-size-param') : '';
    if (sizeControl && sizeParam) {
      sizeControl.querySelectorAll(`input[name="${sizeParam}"]`).forEach((input) => {
        const row = input.closest('label') || input.parentElement;
        const node = row ? row.querySelector('[data-ms-size-count]') : null;
        if (!node) return;
        const wanted = String(input.value).trim().toLowerCase();
        let total = 0;
        cards.forEach((card) => {
          const match = sizeMatch(card, [wanted]);
          if (!match) return;
          if (availabilityMatch(stockFor(card), match.sizeValue, availability)) total += 1;
        });
        node.textContent = ` (${total})`;
        node.classList.toggle('ms-facet-count--empty', total === 0);
      });
    }
  };

  // A size the catalogue cannot sell in any state - no ready variant and none that sells on with no
  // stock, so nothing to pre-order either - is not a choice, so it is struck through and disabled.
  // Two rules this must respect: a size that is ticked stays usable (otherwise the shopper cannot
  // undo their own selection), and nothing is disabled from a partial load, because a size that
  // looks unsellable on one page may be sellable on a page that never arrived.
  const renderSizeAvailability = (cards, sizes, availability, complete) => {
    if (!complete) return;
    const panel = document.querySelector('[data-ms-size-filter]');
    const param = panel && panel.getAttribute('data-ms-size-param');
    if (!panel || !param) return;

    panel.querySelectorAll(`input[name="${param}"]`).forEach((input) => {
      const wanted = String(input.value).trim().toLowerCase();
      let orderable = false;
      cards.forEach((card) => {
        if (orderable) return;
        const match = sizeMatch(card, [wanted]);
        if (!match) return;
        const stock = stockFor(card);
        if (availabilityMatch(stock, match.sizeValue, ['1']) || availabilityMatch(stock, match.sizeValue, ['0'])) {
          orderable = true;
        }
      });

      const row = input.closest('label') || input.parentElement;
      const soldOut = !orderable && !input.checked;
      input.disabled = soldOut;
      if (row) {
        row.classList.toggle('is-sold-out', soldOut);
        row.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
        if (soldOut) row.setAttribute('title', 'Sold out');
      }
    });
  };

  // applies both filters to one card and reports whether it stays in the grid
  const applyFilters = (card, sizes, availability) => {
    let sizeValue = null;
    let entries = [];

    if (sizes && sizes.length) {
      const match = sizeMatch(card, sizes);
      if (!match) {
        card.hidden = true;
        return false;
      }
      sizeValue = match.sizeValue;
      entries = match.entries;
    }

    // Prefer the card's own map: it is size-aware and covers every variant. The rendered badges
    // are the fallback only for products with no size option at all - they describe just the
    // card's default variant, so trusting them over the map dropped products that do have
    // ready-to-ship sizes.
    const stock = stockFor(card);
    if (!availabilityMatch(stock, sizeValue, availability)) {
      card.hidden = true;
      return false;
    }

    if (!entries.length) {
      card.hidden = false;
      return true;
    }

    const handle = handleFrom(cardLink(card).getAttribute('href'));
    const primary = entries[0];

    // the card opens the PDP on a colour that has the size, not the default colour
    if (handle) cardLink(card).setAttribute('href', `/products/${handle}?variant=${primary.variantId}`);
    card.hidden = false;

    if (primary.color.image) {
      const image = card.querySelector('.ms-card__image--primary');
      if (image) {
        const base = primary.color.image;
        image.setAttribute('src', imageUrl(base, 600));
        image.setAttribute('srcset',
          `${imageUrl(base, 240)} 240w, ${imageUrl(base, 360)} 360w, ${imageUrl(base, 540)} 540w, ${imageUrl(base, 720)} 720w`);
      }
      const secondary = card.querySelector('.ms-card__image--secondary');
      if (secondary) secondary.removeAttribute('srcset');
    }

    const wrap = card.querySelector('[data-card-variants]');
    const note = card.querySelector('[data-card-variants-note]');
    const row = card.querySelector('[data-card-swatches]');
    if (!wrap || !row) return true;

    // A product with no colour option still needs its size honoured - the card link above and
    // the hiding are already done - but it has no colours to show, so no swatches and no count.
    const named = entries.filter((entry) => entry.color && entry.color.name);
    if (!named.length) return true;

    if (note) {
      note.textContent = `${compactSize(sizeValue)} available in ${named.length} ${named.length === 1 ? 'colour' : 'colours'}`;
      note.hidden = false;
    }
    row.innerHTML = named
      .map((entry) => swatchHtml(handle, entry.color, entry.variantId, entry === primary))
      .join('');
    wrap.hidden = false;
    return true;
  };

  // --- loading the rest of the collection ------------------------------------------------
  const parseFragment = (html) => {
    const doc = new DOMParser().parseFromString(`<div class="${GRID_CLASS}">${html}</div>`, 'text/html');
    const grid = doc.querySelector(GRID);
    return {
      cards: Array.from(doc.querySelectorAll(CARD)),
      pages: grid ? Number(grid.dataset.msPages || 0) : 0,
      page: grid ? Number(grid.dataset.msPage || 0) : 0,
    };
  };

  const setStatus = (text) => {
    const grid = document.querySelector(GRID);
    if (!grid) return;
    let line = document.querySelector('[data-ms-grid-status]');
    if (!text) {
      if (line) line.remove();
      return;
    }
    if (!line) {
      line = document.createElement('p');
      line.className = 'ms-subtitle';
      line.setAttribute('data-ms-grid-status', '');
      line.setAttribute('aria-live', 'polite');
      grid.parentNode.insertBefore(line, grid);
    }
    line.textContent = text;
  };

  // The URL the shopper is on carries the filters they picked. The pages we load carry the price
  // and sort with them, but never availability: that facet calls a pre-order product "in stock",
  // so the whole catalogue is classified in the browser instead (see availabilityMatch).
  const buildPageParams = (sectionId) => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    for (const key of Array.from(params.keys())) {
      if (isSizeParam(key) || key === AVAILABILITY) params.delete(key);
    }
    if (sectionId) params.set('section_id', sectionId);
    return params;
  };

  const fetchPage = async (base, page, sectionId) => {
    const params = buildPageParams(sectionId);
    params.set('page', String(page));
    const response = await fetch(`${base}?${params.toString()}`, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`collection page ${page} responded ${response.status}`);
    return parseFragment(await response.text());
  };

  const loadAllMatches = async (sizes, availability) => {
    const grid = document.querySelector(GRID);
    if (!grid || grid.dataset.msLoaded === 'true') return;
    if (grid.dataset.msServerFiltered === 'true') return; // the facet already filtered server-side

    const servedPages = Number(grid.dataset.msPages || 1);
    const current = Number(grid.dataset.msPage || 1);
    const sectionId = grid.dataset.msSection || '';
    const base = grid.dataset.msCollection || window.location.pathname;
    const sort = new URLSearchParams(window.location.search).get('sort_by') || '';

    // With availability chosen the served pagination describes the facet's set, not ours, so the
    // true page count has to come from a page fetched without it. Without that filter the served
    // count is already right.
    const needsTrueCount = availability.length > 0;
    if (!needsTrueCount && !(servedPages > 1)) return;

    grid.dataset.msLoaded = 'true';

    const cacheKey = `${CACHE_PREFIX}${base}|${readFilterKey()}|${sort}|${sectionId}`;
    const byPage = new Map();
    let complete = false;

    // A cached set is only usable while it is fresh. Entries are stamped, expire after
    // CACHE_TTL_MS, and a corrupt one is dropped rather than left to break every later load.
    let cached = null;
    try {
      cached = sessionStorage.getItem(cacheKey);
    } catch (error) {
      cached = null;
    }
    let cachedPages = null;
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed && parsed.pages && Date.now() - Number(parsed.at || 0) < CACHE_TTL_MS) cachedPages = parsed.pages;
      } catch (error) {
        cachedPages = null;
      }
    }

    try {
      if (cachedPages) {
        // A snapshot that does not cover every page would silently drop matches, so a gap means
        // the entry is unusable and the pages are fetched instead.
        const stored = Object.keys(cachedPages).map(Number);
        let covers = stored.length > 0;
        for (let page = 1; page <= servedPages; page += 1) {
          const entry = cachedPages[page];
          if (stored.indexOf(page) === -1 || !Array.isArray(entry) || !entry.length) {
            covers = false;
            break;
          }
        }
        if (!covers) cachedPages = null;
      }
      if (cachedPages) {
        Object.entries(cachedPages).forEach(([page, cards]) => {
          byPage.set(Number(page), cardsFromHtml(cards.join('')));
        });
        complete = true;
      } else {
        if (cached) {
          try {
            sessionStorage.removeItem(cacheKey);
          } catch (error) {
            /* ignore */
          }
        }
        let pages = servedPages;
        if (needsTrueCount) {
          const first = await fetchPage(base, 1, sectionId);
          byPage.set(1, first.cards);
          if (first.pages) pages = first.pages;
        }
        const lastPage = Math.min(pages, MAX_PAGES);
        const wanted = [];
        for (let page = 1; page <= lastPage; page += 1) {
          if (page === 1 && byPage.has(1)) continue;
          if (!needsTrueCount && page === current) {
            byPage.set(current, Array.from(grid.querySelectorAll(CARD)));
            continue;
          }
          wanted.push(page);
        }
        const results = await Promise.all(wanted.map((page) => fetchPage(base, page, sectionId)));
        results.forEach((result, index) => byPage.set(wanted[index], result.cards));
        if (!needsTrueCount) byPage.set(current, Array.from(grid.querySelectorAll(CARD)));
        complete = pages <= MAX_PAGES;
        const payload = { at: Date.now(), pages: {} };
        for (let page = 1; page <= lastPage; page += 1) {
          const cards = byPage.get(page) || [];
          if (cards.length) payload.pages[page] = cards.map((card) => card.outerHTML);
        }
        sessionStorage.setItem(cacheKey, JSON.stringify(payload));
      }
    } catch (error) {
      // network hiccup or storage full: keep whatever arrived and leave the pager usable
      complete = false;
    }

    const ordered = [];
    const seen = new Set();
    byPage.forEach((cards) => {
      cards.forEach((card) => {
        const key = cardKey(card);
        if (!key || seen.has(key)) return;
        seen.add(key);
        ordered.push(card);
      });
    });
    if (!ordered.length) return;

    // rebuild: the server returned each page in the active sort order, so walking the pages in
    // order preserves the order the shopper expects
    const kept = [];
    ordered.forEach((card) => {
      if (applyFilters(card, sizes, availability)) kept.push(card);
    });

    grid.innerHTML = '';
    kept.forEach((card) => grid.appendChild(card));

    // the loaded set is now the authority for every count on the page
    renderFacetCounts(ordered, sizes, availability);
    renderSizeAvailability(ordered, sizes, availability, complete);

    const label = readActiveSizeLabel() || (sizes.length ? compactSize(sizes[0]) : '');
    const pager = document.querySelector('.ms-pagination');
    const pill = document.querySelector(PILL);

    if (!kept.length) {
      const empty = document.createElement('div');
      empty.className = 'ms-empty';
      empty.style.gridColumn = '1/-1';
      empty.innerHTML = '<h2>No styles match</h2><p>Try another size, or clear a filter to widen the search.</p>';
      grid.appendChild(empty);
      setStatus('');
      if (pill) pill.textContent = `0 styles`;
      return;
    }

    const wantsReady = availability.indexOf('1') > -1;
    const wantsPreorder = availability.indexOf('0') > -1;
    const availabilityLabel = wantsReady && wantsPreorder
      ? 'ready-to-ship or pre-order'
      : wantsReady ? 'ready-to-ship' : wantsPreorder ? 'pre-order' : '';
    const plural = kept.length === 1 ? 'style' : 'styles';
    const qualifier = availabilityLabel ? `${availabilityLabel} ` : '';
    const scope = label ? ` with size ${label}` : '';

    if (complete) {
      // every matching product is in the grid now, so the pager would lie about there being more
      if (pager) pager.setAttribute('hidden', '');
      setStatus(`Showing all ${kept.length} ${qualifier}${plural}${scope}`);
    } else {
      setStatus(`Showing ${kept.length} ${qualifier}${plural}${scope} — more pages below`);
    }

    // the header pill counts what the server returned; with our own filtering on top that number
    // no longer matches the grid, so state the real one
    if (pill) pill.textContent = `${kept.length} ${kept.length === 1 ? 'style' : 'styles'}`;
    // settle shopify:collection:update with the number of products that actually matched
    if (window.MStoreStandardEvents) window.MStoreStandardEvents.collectionCount(kept.length);
  };

  let lastLoaderError = null;

  const run = () => {
    const sizes = readActiveSize();
    const availability = readActiveAvailability();
    if (!sizes.length && !availability.length) return; // no filter we refine: cards stay as rendered
    document.querySelectorAll(CARD).forEach((card) => applyFilters(card, sizes, availability));
    loadAllMatches(sizes, availability).catch((error) => {
      // The cards already in the DOM are filtered, so the grid stays honest. Say that this is only
      // the page on screen rather than leaving the server's count standing, and keep the reason for
      // the console instead of failing silently.
      lastLoaderError = String((error && error.message) || error);
      const shown = document.querySelectorAll(`${CARD}:not([hidden])`).length;
      const label = readActiveSizeLabel();
      setStatus(`Showing ${shown} ${shown === 1 ? 'style' : 'styles'} on this page${label ? ` with size ${label}` : ''} — the rest could not be loaded`);
    });
  };

  // --- keeping the size selection across the other panels' Apply buttons -------------------
  const compactLower = (value) => compactSize(value).toLowerCase();

  const activeSizeValues = () => {
    const params = new URLSearchParams(window.location.search);
    const values = [];
    for (const [key, value] of params.entries()) {
      if (isSizeParam(key) && value) values.push(String(value).trim());
    }
    return values;
  };

  // Which size checkboxes should be ticked, and which active sizes have no checkbox in the list.
  // Pure, so the matching can be tested without a DOM.
  const planSizeSync = (activeValues, optionValues) => {
    const check = [];
    const extra = [];
    activeValues.forEach((active) => {
      const hit =
        optionValues.find((option) => option === active) ||
        optionValues.find((option) => compactLower(option) === compactLower(active));
      if (hit) {
        if (check.indexOf(hit) === -1) check.push(hit);
      } else if (extra.indexOf(active) === -1) {
        extra.push(active);
      }
    });
    return { check, extra };
  };

  // Without a facet, Liquid cannot read the chosen size back out of the URL, so after any submit
  // the checkbox is unticked and clicking Apply in another panel (Availability, Price) would drop
  // the size. Tick it here, and carry it in a hidden input when the value is not in the list.
  const syncSizeControls = () => {
    const panel = document.querySelector('[data-ms-size-filter]');
    if (!panel) return;

    const active = activeSizeValues();
    if (!active.length) return;

    const boxes = Array.from(panel.querySelectorAll('input[type="checkbox"]'));
    const { check, extra } = planSizeSync(active, boxes.map((box) => box.value));
    boxes.forEach((box) => {
      box.checked = check.indexOf(box.value) > -1;
    });

    const summary = panel.querySelector('summary');
    if (summary) summary.textContent = `Size · ${active.length}`;

    const form = panel.closest('form');
    const param = panel.getAttribute('data-ms-size-param') || '';
    if (!form || !param) return;

    form.querySelectorAll('[data-ms-size-carry]').forEach((input) => input.remove());
    extra.forEach((value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = param;
      input.value = value;
      input.setAttribute('data-ms-size-carry', '');
      form.appendChild(input);
    });
  };

  // --- modern filter behaviour: apply as you go, and show what is active --------------------
  const DRAWER_QUERY = '(max-width: 760px)';
  const drawerMode = () => Boolean(document.querySelector('[data-ms-filter-open]')) && window.matchMedia(DRAWER_QUERY).matches;
  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  // The label a shopper would recognise for a ticked row. Size values keep their EU bracket -
  // "C7 (23-24)" - so the chip says exactly which size is in force; for other rows the trailing
  // result count is dropped ("In stock (120)" -> "In stock").
  const chipLabel = (input) => {
    const row = input.closest('label') || input.parentElement;
    const span = row ? row.querySelector('span') : null;
    const text = (span ? span.textContent : input.value || '').trim();
    return isSizeParam(input.name) ? text : text.replace(/\s*\(\d+\)\s*$/, '');
  };

  // the same page without one filter (and back on page 1, which is what a new filter means);
  // called with null it means clear everything - a link that clears nothing would be worse than
  // no link at all
  const urlWithFilters = (remove) => {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    if (remove === null) {
      for (const key of Array.from(params.keys())) {
        if (key.indexOf('filter.') === 0) params.delete(key);
      }
    } else if (remove && remove.price) {
      for (const key of Array.from(params.keys())) {
        if (key.indexOf('filter.v.price') === 0) params.delete(key);
      }
    } else if (remove && remove.name) {
      const remaining = params.getAll(remove.name).filter((value) => value !== remove.value);
      params.delete(remove.name);
      remaining.forEach((value) => params.append(remove.name, value));
    }
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  };

  const renderActiveFilters = () => {
    const holder = document.querySelector('[data-ms-active-filters]');
    const form = document.querySelector('.ms-toolbar--collection');
    if (!holder || !form) return;

    const chips = [];
    form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach((input) => {
      const label = chipLabel(input);
      chips.push(
        `<a class="ms-chip" href="${urlWithFilters({ name: input.name, value: input.value })}" aria-label="Remove filter ${escapeHtml(label)}">${escapeHtml(label)}<span aria-hidden="true">&times;</span></a>`
      );
    });

    const priceMin = form.querySelector('input[name$="[gte]"], input[name$=".gte"]');
    const priceMax = form.querySelector('input[name$="[lte]"], input[name$=".lte"]');
    const min = priceMin && priceMin.value.trim() !== '' ? priceMin.value.trim() : '';
    const max = priceMax && priceMax.value.trim() !== '' ? priceMax.value.trim() : '';
    if (min || max) {
      const label = min && max ? `$${min} - $${max}` : max ? `Under $${max}` : `Over $${min}`;
      chips.push(
        `<a class="ms-chip" href="${urlWithFilters({ price: true })}" aria-label="Remove price filter ${escapeHtml(label)}">${escapeHtml(label)}<span aria-hidden="true">&times;</span></a>`
      );
    }

    if (!chips.length) {
      holder.innerHTML = '';
      holder.hidden = true;
      return;
    }
    chips.push(`<a class="ms-chip ms-chip--clear" href="${urlWithFilters(null)}">Clear all</a>`);
    holder.innerHTML = chips.join('');
    holder.hidden = false;
    // the toolbar's own Clear link is redundant once the chips carry the same job - removed
    // rather than hidden, because [hidden] loses to the display rule on .ms-btn
    document.querySelectorAll('.ms-clear-filters').forEach((link) => link.remove());
  };

  // apply on change instead of hunting for the panel's Apply button; the number inputs wait for
  // a commit (blur or Enter) so a half-typed price never fires a request
  const wireInstantFilters = () => {
    const form = document.querySelector('.ms-toolbar--collection');
    if (!form || form.dataset.msInstant === 'true') return;
    form.dataset.msInstant = 'true';
    document.documentElement.classList.add('ms-js');

    let timer = null;
    const queueSubmit = () => {
      // phones apply everything at once from the filter panel's Show results button
      if (drawerMode()) return;
      window.clearTimeout(timer);
      // shopify:collection:update - the buyer changed a filter or the sort order. Dispatched from the
      // filter form, and settled with the match count once the grid is final. productFilters is left
      // out: our parameters are option values, and the documented payload wants semantic filter
      // objects, so sending a guess would tell listeners something untrue.
      if (window.MStoreStandardEvents) {
        const toolbar = form.closest('.ms-toolbar--collection') || form;
        const sortSelect = form.querySelector('select[name="sort_by"]');
        window.MStoreStandardEvents.collectionUpdate(form, {
          collection: {
            id: toolbar.getAttribute('data-ms-collection-id') || undefined,
            handle: toolbar.getAttribute('data-ms-collection-handle') || undefined,
            productsCount: Number(toolbar.getAttribute('data-ms-collection-count')) || undefined,
          },
          sortKey: sortSelect && sortSelect.value ? sortSelect.value : undefined,
        });
      }
      timer = window.setTimeout(() => form.submit(), 250);
    };
    form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
      input.addEventListener('change', queueSubmit);
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.addEventListener('change', queueSubmit);
    });
  };

  // The chat widget floats at the very top of the stacking order and covers an open filter panel
  // (the panel cannot out-rank it, and it is not ours to restyle). So the same trick the theme
  // uses for its drawers: while a panel is open the widget steps aside.
  const wireFilterPanelLock = () => {
    const panels = Array.from(document.querySelectorAll('.ms-filter'));
    if (!panels.length) return;
    const sync = () => {
      // the phone filter panel lists every group open or closed; the lock there follows the panel itself
      if (drawerMode()) return;
      const anyOpen = panels.some((panel) => panel.open);
      document.body.classList.toggle('ms-filter-open', anyOpen);
    };
    panels.forEach((panel) => panel.addEventListener('toggle', sync));
    sync();
  };

  // Phones: every filter in one full-screen panel, opened from the Filter button and applied together with
  // Show results - ticking a box no longer reloads the page, so several can be chosen in one go. The
  // groups are the same <details> as the desktop pills, restyled as sections by the CSS.
  const wireFilterDrawer = () => {
    const toggle = document.querySelector('[data-ms-filter-open]');
    const drawer = document.querySelector('[data-ms-filter-drawer]');
    if (!toggle || !drawer) return;

    const count = document.querySelectorAll('[data-ms-active-filters] .ms-chip:not(.ms-chip--clear)').length;
    const badge = toggle.querySelector('[data-ms-filter-count]');
    if (badge) {
      badge.textContent = String(count);
      badge.hidden = count === 0;
    }

    const setOpen = (open) => {
      drawer.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
        drawer.setAttribute('aria-label', 'Filters');
        if (!drawer.querySelector('.ms-filter[open]')) {
          const first = drawer.querySelector('[data-ms-size-filter]') || drawer.querySelector('.ms-filter');
          if (first) first.open = true;
        }
      } else {
        ['role', 'aria-modal', 'aria-label'].forEach((name) => drawer.removeAttribute(name));
      }
      // ms-lock stops the page scrolling behind and moves chat launchers aside, as for the cart drawer
      document.body.classList.toggle('ms-lock', open);
      document.body.classList.toggle('ms-filter-open', open);
      const focusTarget = open ? drawer.querySelector('[data-ms-filter-close]') : toggle;
      if (focusTarget) focusTarget.focus();
    };

    toggle.addEventListener('click', () => setOpen(true));
    drawer.querySelectorAll('[data-ms-filter-close]').forEach((button) => button.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) setOpen(false);
    });
    const media = window.matchMedia(DRAWER_QUERY);
    const onResize = (event) => { if (!event.matches && drawer.classList.contains('is-open')) setOpen(false); };
    if (media.addEventListener) media.addEventListener('change', onResize);
  };

  wireInstantFilters();
  wireFilterPanelLock();
  syncSizeControls();
  renderActiveFilters();
  wireFilterDrawer();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();

  window.MStoreCollectionVariants = {
    run,
    applyFilters,
    applySize: applyFilters, // kept for existing callers
    sizeMatch,
    availabilityMatch,
    badgeAvailability,
    readActiveSize,
    readActiveAvailability,
    lastError: () => lastLoaderError,
    compactSize,
    loadAllMatches,
  };
})();
